/**
 * Centralised HTTP status code constants used across interceptors,
 * services, and fabricated `ApiResponse` error envelopes.
 *
 * Reference these instead of sprinkling magic numbers so that:
 *   1. A future reader can grep `HttpStatus.TooManyRequests` and find
 *      every rate-limit branch in one shot.
 *   2. IDE autocomplete surfaces the full set when writing new branches.
 *   3. Comparison typos (e.g. `=== 410` vs `=== 401`) stop being a risk.
 *
 * Only the codes the project actually consumes live here — extend the
 * list when a new branch is introduced. Do NOT import from
 * `@angular/common/http` for this: Angular does not ship an
 * `HttpStatusCode` enum.
 *
 * Special case: `NetworkError = 0` is the Angular `HttpErrorResponse`
 * convention for "the request never reached the server" (DNS failure,
 * CORS rejection, backend down). Treating it as a named status lets the
 * interceptor branches read naturally.
 */
export const HttpStatus = {
  /** Request never reached the server (Angular `HttpErrorResponse` sentinel). */
  NetworkError: 0,

  // 2xx — success
  Ok: 200,
  NoContent: 204,

  // 4xx — client errors
  BadRequest: 400,
  Unauthorized: 401,
  Forbidden: 403,
  NotFound: 404,
  Conflict: 409,
  Gone: 410,
  PayloadTooLarge: 413,
  TooManyRequests: 429,

  // 5xx — server errors
  InternalServerError: 500,
  BadGateway: 502,
  ServiceUnavailable: 503,
  GatewayTimeout: 504,
} as const;

/** The numeric literal type of any value in {@link HttpStatus}. */
export type HttpStatusCode = (typeof HttpStatus)[keyof typeof HttpStatus];
